import { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Box,
  IconButton,
  Tooltip,
  useTheme,
  Alert,
  Paper,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
} from "@mui/icons-material";
import FancyPaper from "../components/FancyPaper";
import { useApp } from "../hooks/useApp";
import { getXmlNodeById, getXmlNodeByRootName, updateXmlNode } from "../services/xmlNode/XmlNodeService";
import ResourceAccess from "../components/security/ResourceAccess";

interface XmlNode {
  id: number;
  name: string;
  required: boolean;
  alwaysRequired: boolean;
  children?: XmlNode[];
}

const ManagementXmlNodes = () => {
  const { authInfo, setLoading, setErrorMsg, setSuccessMsg } = useApp();
  const theme = useTheme();
  const [facturaElectronicaData, setFacturaElectronicaData] = useState<XmlNode | null>(null);
  const [notaCreditoData, setNotaCreditoData] = useState<XmlNode | null>(null);
  const [expandedItemsFactura, setExpandedItemsFactura] = useState<Set<number>>(new Set());
  const [expandedItemsNota, setExpandedItemsNota] = useState<Set<number>>(new Set());

  /**
   * Efecto inicial para carga de registros
   */
  useEffect(() => {
    const dataInit = async () => {
      await handleFetchData();
    };
    dataInit();
  }, []);

  /**
   * Método encargado de buscar registros
   */
  const handleFetchData = async () => {
    setLoading && setLoading(true);
    try {
      // Cargar ambos árboles
      const facturaData = await getXmlNodeByRootName('FacturaElectronica');
      const notaData = await getXmlNodeByRootName('NotaCreditoElectronica');
      
      if (facturaData) {
        setFacturaElectronicaData(facturaData);
        // Expandir el nodo raíz por defecto
        setExpandedItemsFactura(new Set([facturaData.id]));
      }
      
      if (notaData) {
        setNotaCreditoData(notaData);
        // Expandir el nodo raíz por defecto
        setExpandedItemsNota(new Set([notaData.id]));
      }
      
      setLoading && setLoading(false);
    } catch (error: any) {
      setLoading && setLoading(false);
      setErrorMsg && setErrorMsg(error.message);
      console.log(error);
    }
  };

  /**
   * Función auxiliar para actualizar un nodo específico en el árbol
   */
  const updateNodeInTree = (tree: XmlNode, targetId: number, newRequired: boolean): XmlNode => {
    if (tree.id === targetId) {
      return {
        ...tree,
        required: newRequired,
      };
    }

    if (tree.children && tree.children.length > 0) {
      return {
        ...tree,
        children: tree.children.map(child => updateNodeInTree(child, targetId, newRequired)),
      };
    }

    return tree;
  };

  /**
   * Método para actualizar el estado de un nodo
   */
  const handleToggleRequired = async (node: XmlNode) => {
    if (node.alwaysRequired) {
      // No permitir cambios si alwaysRequired es true
      return;
    }

    // Determinar a cuál árbol pertenece el nodo
    const belongsToFactura = facturaElectronicaData && findNodeInTree(facturaElectronicaData, node.id);
    const belongsToNota = notaCreditoData && findNodeInTree(notaCreditoData, node.id);
    
    if (!belongsToFactura && !belongsToNota) {
      setErrorMsg && setErrorMsg("No hay datos del árbol disponibles");
      return;
    }

    setLoading && setLoading(true);
    try {
      let updatedTree;
      
      if (belongsToFactura && facturaElectronicaData) {
        updatedTree = updateNodeInTree(facturaElectronicaData, node.id, !node.required);
      } else if (belongsToNota && notaCreditoData) {
        updatedTree = updateNodeInTree(notaCreditoData, node.id, !node.required);
      }
      
      if (updatedTree) {
        // Enviar toda la estructura actualizada al servidor
        const result = await updateXmlNode(updatedTree, authInfo?.username || 'system');
        if (result) {
          setSuccessMsg && setSuccessMsg("Nodo actualizado correctamente");
          // Refresh data
          await handleFetchData();
        }
      }
      
      setLoading && setLoading(false);
    } catch (error: any) {
      setLoading && setLoading(false);
      setErrorMsg && setErrorMsg(error.message);
      console.log(error);
    }
  };

  /**
   * Función auxiliar para encontrar un nodo en el árbol
   */
  const findNodeInTree = (tree: XmlNode, targetId: number): boolean => {
    if (tree.id === targetId) {
      return true;
    }
    
    if (tree.children && tree.children.length > 0) {
      return tree.children.some(child => findNodeInTree(child, targetId));
    }
    
    return false;
  };

  /**
   * Método para manejar la expansión de nodos
   */
  const handleToggleExpanded = (nodeId: number, treeType: 'factura' | 'nota') => {
    if (treeType === 'factura') {
      const newExpanded = new Set(expandedItemsFactura);
      if (newExpanded.has(nodeId)) {
        newExpanded.delete(nodeId);
      } else {
        newExpanded.add(nodeId);
      }
      setExpandedItemsFactura(newExpanded);
    } else {
      const newExpanded = new Set(expandedItemsNota);
      if (newExpanded.has(nodeId)) {
        newExpanded.delete(nodeId);
      } else {
        newExpanded.add(nodeId);
      }
      setExpandedItemsNota(newExpanded);
    }
  };

  /**
   * Método recursivo para renderizar nodos del árbol
   */
  const renderTreeNode = (node: XmlNode, level: number = 0, treeType: 'factura' | 'nota' = 'factura'): JSX.Element => {
    const expandedItems = treeType === 'factura' ? expandedItemsFactura : expandedItemsNota;
    const isExpanded = expandedItems.has(node.id);
    const hasChildren = node.children && node.children.length > 0;

    return (
      <Box key={node.id}>
        <ListItem
          sx={{
            pl: 2 + level * 3,
            py: 1,
            borderLeft: level > 0 ? `2px solid ${theme.palette.divider}` : "none",
            "&:hover": {
              backgroundColor: theme.palette.action.hover,
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={() => handleToggleExpanded(node.id, treeType)}
                sx={{ mr: 1 }}
              >
                {isExpanded ? <ExpandMoreIcon /> : <ChevronRightIcon />}
              </IconButton>
            ) : (
              <Box sx={{ width: 32, height: 32, mr: 1 }} />
            )}
          </Box>
          
          <ListItemText
            primary={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    mr: 2,
                    fontWeight: level === 0 ? 'bold' : 'normal',
                    fontSize: level === 0 ? '1.1rem' : '1rem',
                    color: theme.palette.text.primary
                  }}
                >
                  {node.name}
                </Typography>
                {node.alwaysRequired && (
                  <Alert 
                    severity="info" 
                    sx={{ 
                      py: 0, 
                      minHeight: 'auto',
                      '& .MuiAlert-message': { py: 0.5, fontSize: '0.75rem' }
                    }}
                  >
                    Siempre requerido
                  </Alert>
                )}
              </Box>
            }
          />
          
          <ResourceAccess isCode={true} pathOrCode={"XMLNODO:WRITE"}>
            <Tooltip
              title={
                node.alwaysRequired
                  ? "Este campo siempre es requerido"
                  : node.required
                  ? "Clic para desbloquear (no requerido)"
                  : "Clic para bloquear (requerido)"
              }
            >
              <Box>
                <IconButton
                  onClick={() => handleToggleRequired(node)}
                  disabled={node.alwaysRequired}
                  color={node.required ? "error" : "success"}
                  sx={{
                    "&:hover": {
                      backgroundColor: node.required
                        ? theme.palette.error.light + "20"
                        : theme.palette.success.light + "20",
                    },
                  }}
                >
                  {node.required ? 
                    <LockIcon sx={{ fontSize: '1.5rem' }} /> : 
                    <LockOpenIcon sx={{ fontSize: '1.5rem' }} />
                  }
                </IconButton>
              </Box>
            </Tooltip>
          </ResourceAccess>
        </ListItem>

        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box>
              {node.children!.map((child) => renderTreeNode(child, level + 1, treeType))}
            </Box>
          </Collapse>
        )}

        {level === 0 && <Divider sx={{ my: 1 }} />}
      </Box>
    );
  };

  return (
    <FancyPaper pagetitle="Control de Nodos XML" prevpage="Control de Nodos XML">
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100],
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.primary }}>
              Instrucciones:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.primary }}>
              • Los nodos con candado rojo (🔒) están marcados como requeridos
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.primary }}>
              • Los nodos con candado verde (🔓) no son requeridos
            </Typography>
            <Typography variant="body2" sx={{ mb: 1, color: theme.palette.text.primary }}>
              • Los nodos marcados como "Siempre requerido" no se pueden modificar
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
              • Haz clic en el candado para cambiar el estado de requerido/no requerido
            </Typography>
          </Paper>
        </Grid>
        {/* Árbol de Factura Electrónica */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            Factura Electrónica
          </Typography>
          {facturaElectronicaData ? (
            <Paper sx={{ 
              p: 1,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary
            }}>
              <List sx={{ 
                width: "100%",
                color: theme.palette.text.primary
              }}>
                {renderTreeNode(facturaElectronicaData, 0, 'factura')}
              </List>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                No hay datos de Factura Electrónica disponibles
              </Typography>
            </Box>
          )}
        </Grid>

        {/* Árbol de Nota de Crédito */}
        <Grid item xs={12} md={6}>
          <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
            Nota de Crédito
          </Typography>
          {notaCreditoData ? (
            <Paper sx={{ 
              p: 1,
              backgroundColor: theme.palette.background.paper,
              color: theme.palette.text.primary
            }}>
              <List sx={{ 
                width: "100%",
                color: theme.palette.text.primary
              }}>
                {renderTreeNode(notaCreditoData, 0, 'nota')}
              </List>
            </Paper>
          ) : (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                No hay datos de Nota de Crédito disponibles
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </FancyPaper>
  );
};

export default ManagementXmlNodes;